/**
 * TanStack Query configuration and the cache-key registry.
 *
 * Keys live here rather than being spelled inline at each call site so that an
 * invalidation after a mutation cannot miss a list because someone wrote the key
 * slightly differently. `queryKeys.users.list(params)` is the only way to name
 * the user list, so `invalidateQueries({ queryKey: queryKeys.users.all })`
 * reliably catches every variant of it.
 */
import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '../api/axiosClient.js';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      /**
       * Retry transient failures only.
       *
       * Retrying a 403 or a 422 wastes a round trip and cannot succeed — the
       * answer will not change. Retrying a 501 is worse: it delays the fixture
       * fallback behind three pointless requests.
       */
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          if (error.status >= 400 && error.status < 500) return false;
          if (error.code === 'not_implemented') return false;
        }
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

/** Every cache key in the app, in one place. */
export const queryKeys = {
  me: ['me'],
  sessions: ['auth', 'sessions'],

  users: {
    all: ['users'],
    list: (params) => ['users', 'list', params],
    detail: (id) => ['users', 'detail', id],
    roles: (id) => ['users', 'roles', id],
    permissionCheck: (id, checks) => ['users', 'permission-check', id, checks],
  },
  roles: {
    all: ['roles'],
    list: () => ['roles', 'list'],
    detail: (id) => ['roles', 'detail', id],
    permissions: (id) => ['roles', 'permissions', id],
    catalogue: ['roles', 'catalogue'],
  },

  leads: {
    all: ['leads'],
    list: (params) => ['leads', 'list', params],
    detail: (id) => ['leads', 'detail', id],
  },
  accounts: {
    all: ['accounts'], list: (p) => ['accounts', 'list', p], detail: (id) => ['accounts', 'detail', id],
    hierarchy: (id, p) => ['accounts', 'hierarchy', id, p], contacts: (id, p) => ['accounts', 'contacts', id, p],
  },
  contacts: {
    all: ['contacts'], list: (p) => ['contacts', 'list', p], detail: (id) => ['contacts', 'detail', id],
    occasions: (d) => ['contacts', 'occasions', d], duplicates: (id, c) => ['contacts', 'duplicates', id, c],
    relationships: (id) => ['contacts', 'relationships', id], accounts: (id) => ['contacts', 'accounts', id],
  },
  opportunities: {
    all: ['opportunities'], list: (p) => ['opportunities', 'list', p], detail: (id) => ['opportunities', 'detail', id],
    stageHistory: (id) => ['opportunities', 'stage-history', id],
    products: (id) => ['opportunities', 'products', id], competitors: (id) => ['opportunities', 'competitors', id],
    contactRoles: (id) => ['opportunities', 'contact-roles', id], timeline: (id) => ['opportunities', 'timeline', id],
  },
  pipeline: {
    board: (p) => ['pipeline', 'board', p], velocity: (p) => ['pipeline', 'velocity', p],
  },
  analytics: { lostOpportunities: (p) => ['analytics', 'lost-opportunities', p] },
  quotations: {
    all: ['quotations'], list: (p) => ['quotations', 'list', p], detail: (id) => ['quotations', 'detail', id],
    versions: (rootId) => ['quotations', 'versions', rootId],
  },
  approvals: { all: ['approvals'], queue: (p) => ['approvals', 'queue', p] },
  activities: {
    all: ['activities'], list: (p) => ['activities', 'list', p], detail: (id) => ['activities', 'detail', id],
    calendar: (p) => ['activities', 'calendar', p],
    feed: (p) => ['activities', 'feed', p],
  },
  followUps: { all: ['follow-ups'], list: (p) => ['follow-ups', 'list', p] },
  campaigns: {
    all: ['campaigns'], list: (p) => ['campaigns', 'list', p], detail: (id) => ['campaigns', 'detail', id],
    members: (id, p) => ['campaigns', 'members', id, p],
  },
  teams: {
    all: ['teams'], list: (p) => ['teams', 'list', p], detail: (id) => ['teams', 'detail', id],
    members: (id, p) => ['teams', 'members', id, p],
  },
  partners: {
    all: ['partners'], list: (p) => ['partners', 'list', p], detail: (id) => ['partners', 'detail', id],
  },
  dashboards: (variant) => ['dashboards', variant],
  targets: { list: (p) => ['targets', 'list', p] },
  reports: {
    leads: (p) => ['reports', 'leads', p],
    opportunities: (p) => ['reports', 'opportunities', p],
    activities: (p) => ['reports', 'activities', p],
    revenue: (p) => ['reports', 'revenue', p],
    forecast: (p) => ['reports', 'forecast', p],
    territories: (p) => ['reports', 'territories', p],
  },
  masterData: {
    all: ['master-data'],
    entities: ['master-data', 'entities'],
    list: (entity, params) => ['master-data', 'list', entity, params],
    detail: (entity, id) => ['master-data', 'detail', entity, id],
    /* Ref-field and scope dropdowns: the same list as `list` but always
       unfiltered and without usage counts, so it is cached once and shared by
       every form that looks up against that entity. */
    options: (entity) => ['master-data', 'options', entity],
  },
  admin: {
    audit: (p) => ['admin', 'audit', p],
    workflows: ['admin', 'workflows'],
    scoringRules: ['admin', 'scoring-rules'],
    securityPolicy: ['admin', 'security-policy'],
  },
  emailTemplates: {
    all: ['email-templates'], list: (p) => ['email-templates', 'list', p],
    detail: (id) => ['email-templates', 'detail', id], folders: ['email-templates', 'folders'],
    mergeFields: ['email-templates', 'merge-fields'],
  },
  email: {
    sent: (p) => ['email', 'sent', p],
  },
  files: {
    attachments: (entityKey, recordId) => ['files', 'attachments', entityKey, recordId],
  },
};
